import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styled from 'styled-components';
import {
  Breakpoints,
  BreakpointWrapper,
  Gallery,
  GallerySlide,
  ArrowButtons,
  Lentils,
} from '@volkswagen-onehub/components-core';
import ScrollHintArrow from '../ScrollHintArrow';

gsap.registerPlugin(ScrollTrigger);

const texts = ['Una edición', 'única: GTI 50', 'aniversario'];

const StickyWrapper = styled.section`
  position: relative;
  width: 100%;
  background-color: #000000;

  overflow-x: clip;
  overscroll-behavior-x: none;
  touch-action: pan-y;

  @supports not (overflow: clip) {
    overflow-x: hidden;
  }

  @media (max-width: 560px) {
    overflow-x: hidden;
  }
`;

const ScrollScene = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  width: 100%;
  overflow: clip;

  @supports not (overflow: clip) {
    overflow: hidden;
  }

  display: flex;
  align-items: center;
  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/gallery-background2?Zml0PWNyb3AlMkMxJndpZD0yNTYwJmhlaT0xNDQwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjRhNjQ=');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (max-width: 959px) {
    display: none;
  }
`;

const FloatingTitle = styled.div`
  color: #ffffff;
  max-width: 40vw;
  height: 100vh;
  font-size: 5vw;
  line-height: 5vw;
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: -0.3vw;
  margin-left: 5vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-right: 0.6ch;

  &[data-variant='desktop'] {
    @media (max-width: 959px) {
      display: none;
    }
  }

  &[data-variant='mobile'] {
    @media (min-width: 960px) {
      display: none;
    }
    @media (max-width: 959px) {
      max-width: 100%;
      margin-left: 0;
      padding: 0 10vw;
      height: auto;
      font-size: 6vh;
      line-height: 13vw;
      margin-top: 12vh;
      margin-bottom: 4vh;
      letter-spacing: clamp(-0.06em, -0.8vw, -0.03em);
      background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/gallery-background2?Zml0PWNyb3AlMkMxJndpZD0yNTYwJmhlaT0xNDQwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjRhNjQ=');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
  }
`;

const TextLineWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 1vw;

  @media screen and (max-width: 959px) {
    display: block;
  }
`;

const RevealOverlay = styled.div`
  position: absolute;
  top: -15%;
  left: 0;
  width: 110%;
  height: 115%;
  background-color: #ffffff;
  transform-origin: left;
`;

const ScrollableContent = styled.div`
  width: 100%;
  display: flex;
  min-height: 200vh;
  padding-bottom: 15vw;

  @media screen and (max-width: 959px) {
    min-height: unset;
    padding-bottom: 0;
  }
`;

const RightColumn = styled.div`
  width: 55vw;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  overflow-x: clip;
  @supports not (overflow: clip) {
    overflow-x: hidden;
  }

  @media screen and (max-width: 959px) {
    width: 100%;
  }
`;

const ImageGrid = styled.div`
  padding: 0 3vw;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem 4rem;
  padding-bottom: 6rem;

  @media screen and (max-width: 959px) {
    display: flex;
    gap: 4vw;
    padding: 5vh 20vw 0 4vw;
    align-items: stretch;
  }
`;

const StyledImageBox = styled.div<{ background: string }>`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-image: url(${(props) => props.background});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  position: relative;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 1rem;
  transform-origin: center;
  will-change: transform;

  &:nth-child(even) {
    transform: translateY(33%);
  }

  @media screen and (max-width: 959px) {
    width: 70vw !important;
    aspect-ratio: 2 / 3;
    align-items: flex-start;
    justify-content: flex-end;
    margin: 0 auto;
    &:nth-child(even) {
      transform: translateY(0);
    }
    &:nth-child(odd) {
      transform: translateY(0);
    }
    font-size: 1rem;
  }
`;

const StyledImageBoxText = styled.div<{ isRightColumn?: boolean }>`
  background-color: ${({ isRightColumn }) => (isRightColumn ? '#C90010' : '#ffffff')};
  color: ${({ isRightColumn }) => (isRightColumn ? '#ffffff' : '#000000')};
  padding: 5px;
  font-size: 1vw;
  margin-bottom: 4px;

  @media screen and (max-width: 959px) {
    font-size: 3.5vw;
  }
`;

const LentilsWrapper = styled.div`
  margin-top: 4vh;
  display: flex;
  justify-content: center;

  & > * {
    background: transparent !important;
  }

  button {
    border-color: #c90010 !important;
  }

  button::before {
    background-color: #c90010 !important;
  }
`;

const ArrowButtonsWrapper = styled.div`
  button {
    background-color: #c90010 !important;
    border-color: #c90010 !important;
  }

  button * {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  button:disabled,
  button[disabled] {
    background-color: #8a010c !important;
    border-color: #8a010c !important;
    opacity: 0.8 !important;
  }
`;

const ScrollHintArrowLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

export const GalleryGti50 = (): React.ReactElement => {
  const sectionRef = useRef<HTMLElement | null>(null);

  const setupTitleAnimations = (container: Element, idSuffix: string) => {
    const lines = container.querySelectorAll<HTMLElement>('[data-line]');
    const overlays = container.querySelectorAll<HTMLElement>('[data-overlay]');

    gsap.set([lines, overlays], { clearProps: 'all' });

    overlays.forEach((ov) => gsap.set(ov, { scaleX: 1, transformOrigin: '0% 50%' }));
    lines.forEach((el) => gsap.set(el, { x: '-100%' }));

    const tl = gsap.timeline({ paused: true, defaults: { ease: 'power2.out' } });

    tl.add('revealStart');
    overlays.forEach((overlay, i) => {
      const line = lines[i];
      const t = `revealStart+=${i * 0.1}`;
      if (overlay && line) {
        tl.fromTo(line, { x: 15 }, { x: 0, duration: 0.25, ease: 'power1.out' }, t);
        tl.to(overlay, { scaleX: 0, duration: 0.35 }, t);
      }
    });

    lines.forEach((el, i) => {
      tl.to(el, { x: 0, skewX: 0, duration: 1, ease: 'power4.out' }, i === 0 ? 0 : '<+=0.1');
    });

    const st = ScrollTrigger.create({
      trigger: sectionRef.current!,
      start: 'top 30%',
      once: true,
      invalidateOnRefresh: true,
      id: `PlayOnEnter-${idSuffix}`,
      onEnter: () => tl.play(0),
    });

    return () => {
      st.kill();
      tl.kill();
    };
  };

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      const desktopTitle = sectionRef.current!.querySelector('[data-variant="desktop"]');
      const mobileTitle = sectionRef.current!.querySelector('[data-variant="mobile"]');
      const mm = gsap.matchMedia();

      mm.add('(min-width: 960px)', () => {
        if (desktopTitle) {
          const dispose = setupTitleAnimations(desktopTitle, 'desktop');
          return () => dispose?.();
        }

        return undefined;
      });

      mm.add('(max-width: 959px)', () => {
        const disposers: Array<() => void> = [];

        if (mobileTitle) {
          disposers.push(setupTitleAnimations(mobileTitle, 'mobile'));
        }

        return () => disposers.forEach((d) => d?.());
      });
      //mm.add('all', (en el caso de que queramos en ambos viewports)
      //   mm.add('(min-width: 560px)', () => {
      //     const images = sectionRef.current!.querySelectorAll('.image-box');
      //     const skewSetter = gsap.quickSetter(images, 'skewY', 'deg');
      //     const clamp = gsap.utils.clamp(-10, 10);

      //     const skewTrigger = ScrollTrigger.create({
      //       trigger: sectionRef.current,
      //       start: 'top top',
      //       end: 'bottom bottom',
      //       scrub: true,
      //       onUpdate: (self) => skewSetter(clamp(self.getVelocity() / -100)),
      //       onLeave: () => skewSetter(0),
      //       onLeaveBack: () => skewSetter(0),
      //       invalidateOnRefresh: true,
      //     });

      //     return () => skewTrigger.kill();
      //   });

      return () => mm.revert();
    }, sectionRef);

    ScrollTrigger.refresh();
    return () => ctx.revert();
  }, []);

  const imageData = [
    {
      text1: 'Techo y retrovisores',
      text2: 'en negro',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/techo-1?Zml0PWNyb3AlMkMxJndpZD0xMzMzJmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjM3ZDY=',
    },
    {
      text1: 'Llanta exclusiva Queenstown',
      text2: 'de 19" en color rojo',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/llantas?Zml0PWNyb3AlMkMxJndpZD0xMzMzJmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjM3ZDY=',
    },
    {
      text1: 'Embellecedores negros',
      text2: 'del sistema de escape',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/embellecedores_negros?Zml0PWNyb3AlMkMxJndpZD03NjImaGVpPTExNDMmZm10PWpwZWcmcWx0PTc5JmJmYz1vZmYmYjRjMg==',
    },
    {
      text1: 'Alerón deportivo',
      text2: 'con logotipo GTI 50',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/aleron?Zml0PWNyb3AlMkMxJndpZD0xMzMzJmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjM3ZDY=',
    },
    {
      text1: 'Tapicería especial',
      text2: 'Cinturones de seguridad en color rojo',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/asiento?Zml0PWNyb3AlMkMxJndpZD0xMzMzJmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjM3ZDY=',
    },
    {
      text1: 'Volante deportivo',
      text2: 'con logotipo GTI 50',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/volante-8?Zml0PWNyb3AlMkMxJndpZD0xMzMzJmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjM3ZDY=',
    },
    {
      text1: 'Embellecedores',
      text2: 'en los umbrales de las puertas',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/embellecedores?Zml0PWNyb3AlMkMxJndpZD0xMzMzJmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjM3ZDY=',
    },
    {
      text1: 'Pedales de aluminio',
      text2: 'con detalles en color rojo',
      src: 'https://assets.volkswagen.com/is/image/volkswagenag/pedales-1?Zml0PWNyb3AlMkMxJndpZD0xMzMzJmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjM3ZDY=',
    },
  ];

  return (
    <StickyWrapper ref={sectionRef} id="gallery-gti50-module">
      <ScrollScene>
        <FloatingTitle data-variant="desktop">
          {texts.map((text, i) => (
            <TextLineWrapper key={`desktop-${i}`} data-line>
              {text}
              <RevealOverlay data-overlay />
            </TextLineWrapper>
          ))}
        </FloatingTitle>
      </ScrollScene>

      <ScrollableContent>
        <RightColumn>
          <FloatingTitle data-variant="mobile">
            {texts.map((text, i) => (
              <TextLineWrapper key={`mobile-${i}`} data-line>
                {text}
                <RevealOverlay data-overlay />
              </TextLineWrapper>
            ))}
          </FloatingTitle>

          <BreakpointWrapper min={Breakpoints.b960}>
            <ImageGrid>
              {imageData.map((item, index) => (
                <StyledImageBox key={index} background={item.src} className="image-box">
                  <StyledImageBoxText isRightColumn={index % 2 !== 0}>{item.text1}</StyledImageBoxText>
                  <StyledImageBoxText isRightColumn={index % 2 !== 0}>{item.text2}</StyledImageBoxText>
                </StyledImageBox>
              ))}
            </ImageGrid>
          </BreakpointWrapper>

          <BreakpointWrapper max={Breakpoints.b960}>
            <Gallery
              id="gallery-gti50"
              bottomElements={
                <>
                  <ArrowButtonsWrapper>
                    <ArrowButtons buttonPreviousAriaLabel="Anterior" buttonNextAriaLabel="Siguiente" />
                  </ArrowButtonsWrapper>

                  <LentilsWrapper>
                    <Lentils />
                  </LentilsWrapper>
                </>
              }
              fadingEdges="auto"
              aria-label="Galería GTI 50"
            >
              {imageData.map((item, index) => (
                <GallerySlide key={index} aria-labelledby={`slide${index}-heading`}>
                  <StyledImageBox background={item.src} className="image-box">
                    <StyledImageBoxText isRightColumn={index % 2 !== 0}>{item.text1}</StyledImageBoxText>
                    <StyledImageBoxText isRightColumn={index % 2 !== 0}>{item.text2}</StyledImageBoxText>
                  </StyledImageBox>
                </GallerySlide>
              ))}
            </Gallery>
          </BreakpointWrapper>
        </RightColumn>
      </ScrollableContent>

      <ScrollHintArrowLayer>
        <ScrollHintArrow trigger={sectionRef} targetSelector="#formulario-module, #timeline-gti-module" color="#ffffff" />
      </ScrollHintArrowLayer>
    </StickyWrapper>
  );
};
